import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaFacebookF, FaInstagram, FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "./theme.css";

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef(null);
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCustomerSatisfactionClick = (e) => {
    e.preventDefault();
    navigate('/', { 
      state: { scrollToTestimonials: true } 
    });
    scrollToTop();
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".footer-heading",
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".footer-heading",
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        }
      );

      gsap.fromTo(".footer-item",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: ".footer-item",
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        }
      );

      gsap.to(".footer-cta", {
        y: -10,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });
    }, footerRef);

    return () => ctx.revert();
  }, []);

  const quickLinks = [
    { name: "Kitchen Gallery", path: "/gallery", description: "View our kitchen gallery" },
    { name: "About Us", path: "/about", description: "Learn about Laminance Cabinetry" },
    { name: "Kitchen Catalog", path: "/catalog", description: "Browse our kitchen catalog" },
    { name: "Contact Us", path: "/contact", description: "Contact Laminance Cabinetry" }
  ];

  const customerSatisfactionItem = { 
    name: "Customer Satisfaction", 
    handler: handleCustomerSatisfactionClick 
  };

  const services = [
    "Custom Kitchen Design",
    "Cabinet Installation",
    "Countertop Services",
    "Kitchen Remodeling",
    "3D Visualization",
    "Free Consultation"
  ];

  const socialLinks = [
    { 
      icon: <FaFacebookF size={18} />, 
      href: "https://www.facebook.com/laminancecabinetry", 
      label: "Facebook",
      rel: "noopener noreferrer"
    },
    { 
      icon: <FaInstagram size={18} />, 
      href: "https://www.instagram.com/laminancecabinetry/", 
      label: "Instagram",
      rel: "noopener noreferrer"
    }
  ];

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "name": "Laminance Cabinetry",
    "alternateName": "Laminance",
    "description": "Premier custom kitchen cabinet and complete kitchen renovation services in New Jersey. Specializing in custom designs, premium materials, and all-inclusive kitchen packages.",
    "url": "https://laminance.com",
    "logo": "https://laminance.com/assets/Images/logoimg.png",
    "image": "https://laminance.com/assets/Images/logoimg.png",
    "telephone": "+18624506069",
    "email": "Contactus@laminance.com",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "418 Rt 23",
      "addressLocality": "Franklin",
      "addressRegion": "NJ",
      "postalCode": "07416",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "41.1220",
      "longitude": "-74.5804"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "09:00",
      "closes": "18:00"
    },
    "sameAs": [
      "https://www.facebook.com/laminancecabinetry",
      "https://www.instagram.com/laminancecabinetry"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+18624506069",
      "contactType": "customer service",
      "email": "Contactus@laminance.com",
      "availableLanguage": ["English"]
    },
    "areaServed": {
      "@type": "State",
      "name": "New Jersey"
    }
  };

  // Local Business Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Laminance Cabinetry",
    "description": "Custom kitchen cabinet design, installation, and complete kitchen renovation services in New Jersey. 5-star rated with premium quality craftsmanship.",
    "url": "https://laminance.com",
    "telephone": "+18624506069",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "418 Rt 23",
      "addressLocality": "Franklin",
      "addressRegion": "NJ",
      "postalCode": "07416",
      "addressCountry": "US"
    }
  };

  // Site Navigation Schema
  const siteNavigationSchema = {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    "name": "Main Navigation",
    "description": "Main navigation links for Laminance Cabinetry website",
    "url": "https://laminance.com",
    "hasPart": [
      ...quickLinks.map(link => ({
        "@type": "WebPage",
        "name": link.name,
        "url": `https://laminance.com${link.path}`,
        "description": link.description
      })),
      {
        "@type": "WebPage",
        "name": "Customer Satisfaction",
        "url": "https://laminance.com/#testimonials",
        "description": "Customer satisfaction and testimonials"
      }
    ]
  };

  return (
    <>
      <Helmet>
        {/* Footer SEO - These help with search engine understanding of your site structure */}
        <link rel="canonical" href="https://laminance.com" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(siteNavigationSchema)}</script>
      </Helmet>

      <footer 
        ref={footerRef} 
        className="text-white relative overflow-hidden"
        style={{
          background: "var(--gradient-footer-rich)",
        }}
        aria-label="Laminance Cabinetry footer"
        itemScope
        itemType="https://schema.org/HomeAndConstructionBusiness"
      >
        <meta itemProp="name" content="Laminance Cabinetry" />
        <meta itemProp="telephone" content="+18624506069" />
        <meta itemProp="email" content="Contactus@laminance.com" />
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none" aria-hidden="true">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, var(--color-white) 1px, transparent 0)`,
              backgroundSize: '50px 50px'
            }}
          ></div>
        </div>

        {/* Animated Orbs - Decorative only */}
        <div 
          className="absolute top-10 left-10 w-4 h-4 rounded-full opacity-60 animate-pulse pointer-events-none"
          style={{ backgroundColor: "var(--color-secondary-500)" }}
          aria-hidden="true"
        ></div>
        <div 
          className="absolute top-1/4 right-20 w-3 h-3 rounded-full opacity-40 animate-pulse delay-1000 pointer-events-none"
          style={{ backgroundColor: "var(--color-accent-500)" }}
          aria-hidden="true"
        ></div>
        <div 
          className="absolute bottom-1/3 left-20 w-2 h-2 rounded-full opacity-50 animate-pulse delay-500 pointer-events-none"
          style={{ backgroundColor: "var(--color-primary-300)" }}
          aria-hidden="true"
        ></div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 relative z-10">
          {/* CTA Section */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div 
              className="inline-flex items-center gap-2 backdrop-blur-sm border px-4 py-2 rounded-full mb-6"
              style={{
                backgroundColor: "var(--bg-primary-500-20)",
                borderColor: "var(--border-primary-30)",
              }}
            >
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: "var(--color-accent-500)" }}
                aria-hidden="true"
              ></div>
              <span 
                className="text-sm font-semibold"
                style={{ color: "var(--color-accent-300)" }}
              >
                Let's Get Started
              </span>
            </div>

            <h2 
              className="bell-mt-primary footer-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              style={{ color: "var(--color-white)" }}
            >
              Build Your{" "}
              <span 
                className="bg-clip-text"
                style={{
                  background: "var(--gradient-accent-cta)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                }}
              >
                Dream Kitchen
              </span>{" "}
              With Laminance Cabinetry
            </h2>
            
            <p 
              className="text-lg mb-8 max-w-2xl mx-auto"
              style={{ color: "var(--color-gray-300)" }}
            >
              Transform your space with our expert design and craftsmanship. Your dream kitchen is just a conversation away.
              Serving all of New Jersey with premium custom kitchen cabinets.
            </p>

            <motion.div className="footer-cta">
              <Link
                to="/contact#contact-form"
                className="inline-flex items-center gap-3 text-white px-8 py-4 rounded-2xl font-bold hover:shadow-2xl transition-all duration-300 group"
                style={{ background: "var(--gradient-accent-cta)" }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToTop}
                aria-label="Schedule free kitchen design consultation"
              >
                <span>Schedule Free Consultation</span>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </motion.div>
          </motion.div>

          {/* Main Footer Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <motion.div 
              className="footer-item"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div 
                className="backdrop-blur-sm rounded-2xl p-6 border h-full"
                style={{
                  background: "var(--gradient-footer-card)",
                  borderColor: "var(--border-white-10)",
                }}
              >
                <img
                  src="/assets/Images/logoimg.png"
                  alt="Laminance Cabinetry - Premium Custom Kitchen Cabinets in New Jersey"
                  className="mb-6 w-40 h-auto filter brightness-0 invert"
                  width="160"
                  height="48"
                  loading="lazy"
                />
                <p 
                  className="mb-6 leading-relaxed"
                  style={{ color: "var(--color-gray-300)" }}
                >
                  Creating beautiful, functional kitchens that transform homes and lives. 
                  Your vision, our expertise - perfect harmony. Serving all of New Jersey with premium custom kitchen cabinets.
                </p>
                
                {/* Social Links */}
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-lg flex items-center justify-center backdrop-blur-sm border transition-all duration-300"
                      style={{
                        backgroundColor: "var(--bg-white-10)",
                        borderColor: "var(--border-white-10)",
                        color: "var(--color-gray-300)",
                      }}
                      whileHover={{ 
                        scale: 1.1, 
                        y: -2,
                        backgroundColor: "var(--overlay-secondary-light)",
                        borderColor: "var(--color-accent-500)"
                      }}
                      whileTap={{ scale: 0.95 }}
                      aria-label={`Follow Laminance Cabinetry on ${social.label}`}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div 
              className="footer-item"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div 
                className="backdrop-blur-sm rounded-2xl p-6 border h-full"
                style={{
                  background: "var(--gradient-footer-card)",
                  borderColor: "var(--border-white-10)",
                }}
              >
                <h3 
                  className="text-xl font-bold mb-6 flex items-center gap-2"
                  style={{ color: "var(--color-white)" }}
                >
                  <FaMapMarkerAlt 
                    style={{ color: "var(--color-accent-500)" }}
                    aria-hidden="true"
                  />
                  Get In Touch
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3 group">
                    <FaMapMarkerAlt 
                      style={{ color: "var(--color-accent-500)", marginTop: "0.25rem" }}
                      aria-hidden="true"
                    />
                    <div>
                      <p style={{ color: "var(--color-gray-300)" }}>Laminance Cabinetry</p>
                      <p 
                        className="text-sm"
                        style={{ color: "var(--color-gray-400)" }}
                        itemProp="address"
                      >
                        418 Rt 23, Franklin, NJ, 07416
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 group">
                    <FaPhone 
                      style={{ color: "var(--color-accent-500)" }}
                      aria-hidden="true"
                    />
                    <a 
                      href="tel:+18624506069" 
                      className="transition-colors hover:underline"
                      style={{ color: "var(--color-gray-300)" }}
                      aria-label="Call Laminance Cabinetry at (862) 450-6069"
                      itemProp="telephone"
                    >
                      (862) 450-6069
                    </a>
                  </div>
                  
                  <div className="flex items-center gap-3 group">
                    <FaEnvelope 
                      style={{ color: "var(--color-accent-500)" }}
                      aria-hidden="true"
                    />
                    <a 
                      href="mailto:Contactus@laminance.com" 
                      className="transition-colors hover:underline"
                      style={{ color: "var(--color-gray-300)" }}
                      aria-label="Email Laminance Cabinetry at Contactus@laminance.com"
                      itemProp="email"
                    >
                      Contactus@laminance.com
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div 
              className="footer-item"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div 
                className="backdrop-blur-sm rounded-2xl p-6 border h-full"
                style={{
                  background: "var(--gradient-footer-card)",
                  borderColor: "var(--border-white-10)",
                }}
              >
                <h3 
                  className="text-xl font-bold mb-6"
                  style={{ color: "var(--color-white)" }}
                >
                  Quick Links
                </h3>
                <ul className="space-y-3">
                  {quickLinks.map((link, index) => (
                    <li key={index}>
                      <Link 
                        to={link.path}
                        className="transition-colors duration-300 flex items-center gap-2 group"
                        onClick={scrollToTop}
                        style={{ color: "var(--color-gray-300)" }}
                        aria-label={link.description}
                      >
                        <div 
                          className="w-1.5 h-1.5 rounded-full transition-opacity group-hover:opacity-100"
                          style={{
                            backgroundColor: "var(--color-accent-500)",
                            opacity: 0,
                          }}
                          aria-hidden="true"
                        ></div>
                        {link.name}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <button
                      onClick={handleCustomerSatisfactionClick}
                      className="w-full text-left transition-colors duration-300 flex items-center gap-2 group bg-transparent border-none cursor-pointer"
                      style={{ color: "var(--color-gray-300)" }}
                      aria-label="View customer satisfaction and testimonials"
                    >
                      <div 
                        className="w-1.5 h-1.5 rounded-full transition-opacity group-hover:opacity-100"
                        style={{
                          backgroundColor: "var(--color-accent-500)",
                          opacity: 0,
                        }}
                        aria-hidden="true"
                      ></div>
                      {customerSatisfactionItem.name}
                    </button>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Services */}
            <motion.div 
              className="footer-item"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div 
                className="backdrop-blur-sm rounded-2xl p-6 border h-full"
                style={{
                  background: "var(--gradient-footer-card)",
                  borderColor: "var(--border-white-10)",
                }}
              >
                <h3 
                  className="text-xl font-bold mb-6"
                  style={{ color: "var(--color-white)" }}
                >
                  Our Kitchen Services
                </h3>
                <ul className="space-y-3">
                  {services.map((service, index) => (
                    <li key={index}>
                      <span 
                        className="flex items-center gap-2"
                        style={{ color: "var(--color-gray-300)" }}
                      >
                        <div 
                          className="w-1.5 h-1.5 rounded-full"
                          style={{
                            backgroundColor: "var(--color-accent-500)",
                            opacity: 0.5,
                          }}
                          aria-hidden="true"
                        ></div>
                        {service}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>

          {/* SEO Keyword Section */}
          <div className="text-center mb-8">
            <p className="text-xs opacity-60" style={{ color: "var(--color-gray-400)" }}>
              <strong>Custom Kitchen Cabinets New Jersey | Acrylic Cabinets | Kitchen Renovation | Cabinet Installation | Modern Kitchen Design</strong>
            </p>
          </div>

          {/* Bottom Bar */}
          <motion.div 
            className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{ borderTop: "1px solid var(--border-white-10)" }}
          >
            <p 
              className="text-sm"
              style={{ color: "var(--color-gray-400)" }}
            >
              © {new Date().getFullYear()} Laminance Cabinetry. All rights reserved. 
              Premium custom kitchen cabinets in New Jersey.
            </p>
            <div className="flex gap-6 text-sm">
              <Link 
                to="/PrivacyPolicy" 
                className="transition-colors hover:underline" 
                onClick={scrollToTop}
                style={{ color: "var(--color-gray-400)" }}
                aria-label="Privacy Policy"
              >
                Privacy Policy
              </Link>
              
            </div>
          </motion.div>
        </div>

        {/* Bottom Gradient */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: "var(--gradient-footer-bottom)" }}
          aria-hidden="true"
        ></div>
      </footer>
    </>
  );
};

export default Footer;